import { setUser, getCurrentScope } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'
import type { Operation } from 'fast-json-patch'
import {
  useState,
  useContext,
  createContext,
  useEffect,
  useMemo,
  useRef,
  Suspense,
} from 'react'
import timezones from 'timezones-list'

import type { Obj, UserId, UserWithPermissions } from '@beaded/models'
import { GlobalRoleType, betaOptions } from '@beaded/models'

import type { HasPermissionContext } from '@beaded/shared-lib/src/Permissions.js'
import {
  hasPermission as rootHasPermission,
  type perms,
} from '@beaded/shared-lib/src/Permissions.js'

import { user as userApi } from 'api'

import { Loading } from 'components/Loading'
import { toaster } from 'components/ui/toaster'

import { selfQueries } from 'lib/queries'
import { queryKeys } from 'lib/queryKeys'
import { queryClient } from 'lib/react-query'
import { resetCookies } from 'lib/resetCookies'
import { resetSocket } from 'lib/socket'
import { clearUIVersion, getUIVersion, setUIVersion } from 'lib/uiVersion'

// Read once at module load -- version never changes without a full page reload
const IS_CLASSIC_UI = getUIVersion() === 'classic'

// Set when switchToUser has already initiated a cross-version navigation, so
// the [currentUser] sync effect doesn't fire a competing reload that would
// strand the switched user on the previous user's route.
let versionNavigationPending = false
const VIEW_AS_RELOAD_KEY = 'bc.viewAs.reloadedAt'

const MODERN_UI_OPTION = betaOptions.find((o) => o.name === 'modernUi')

function resolveModernUiPref(
  prefs: UserWithPermissions['prefs'],
): boolean | undefined {
  if (MODERN_UI_OPTION?.globallyEnabled) return true
  return prefs?.beta?.modernUi
}

export { betaOptions }

// -- Auth context types ------------------------------------------------

export interface IAuthContext {
  authLoaded: boolean
  currentUser: UserWithPermissions | null
  permissions: UserWithPermissions['permissions'] | null
  global: GlobalPerms

  login: (
    username: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<any>
  logout: () => Promise<void>
  switchToUser: (username: string) => Promise<UserWithPermissions | undefined>
  startViewAs: (email: string) => Promise<void>
  exitViewAs: () => Promise<void>
  hasPermission: (
    action: keyof typeof perms,
    context: HasPermissionContext,
  ) => boolean
  routing: {
    home: string
    canViewNavNode: Obj
  }
  patchUserMutation: UseMutationResult<any, unknown, Operation[], unknown>
  hasGlobalViewPerm: boolean
  hasGlobalEditPerm: boolean
  hasGlobalAdminPerm: boolean
}

export type GlobalPerms = {
  view: boolean
  edit: boolean
  admin: boolean
  cableAdmin: boolean
  loggerAdmin: boolean
}

export const authInitialState: IAuthContext = {
  authLoaded: false,
  currentUser: null,
  global: {
    view: false,
    edit: false,
    admin: false,
    cableAdmin: false,
    loggerAdmin: false,
  },
  permissions: {},
  login: async () => {},
  logout: async () => {},
  switchToUser: async () => undefined,
  startViewAs: async () => {},
  exitViewAs: async () => {},
  routing: {
    home: '/',
    canViewNavNode: {
      '/cables': false,
    },
  },
  patchUserMutation: {} as unknown as UseMutationResult<
    any,
    unknown,
    Operation[],
    unknown
  >,
  hasPermission: () => false,
  hasGlobalViewPerm: false,
  hasGlobalEditPerm: false,
  hasGlobalAdminPerm: false,
}

const authContext = createContext<IAuthContext>(authInitialState)

export function ProvideAuthContext({
  children,
}: {
  children: React.ReactNode
}) {
  const auth: IAuthContext = useProvideAuthContext()
  return (
    <Suspense fallback={<Loading />}>
      <authContext.Provider value={auth}>{children}</authContext.Provider>
    </Suspense>
  )
}

export const useAuthContext = () => {
  return useContext(authContext)
}

function useProvideAuthContext() {
  // --- Core data: useQuery replaces manual polling ---
  const selfQuery = useQuery(selfQueries.me())
  const currentUser: UserWithPermissions | null = selfQuery.data?.user ?? null

  // One-way latch: once first fetch completes, stays true through logout/cache-clear.
  const [authLoaded, setAuthLoaded] = useState(false)
  useEffect(() => {
    if (!selfQuery.isPending) setAuthLoaded(true)
  }, [selfQuery.isPending])

  // Sync UIVersion from server-side prefs on every user change so that
  // switch-user flows always reload with the correct version, regardless of
  // what the switch API response contained.
  useEffect(() => {
    if (!currentUser) return
    const modernUi = resolveModernUiPref(currentUser.prefs)
    if (modernUi === true) {
      setUIVersion('modern')
    } else if (modernUi === false) {
      setUIVersion('classic')
    } else {
      clearUIVersion()
    }
    // IS_CLASSIC_UI was captured once at module load. If the version
    // now differs (e.g. fresh login with a modernUi pref), force a full
    // reload so all module-level constants pick up the correct value.
    // Skip when switchToUser already initiated a redirect to '/' -- a
    // reload here would win the race and keep the previous user's route.
    const isNowClassic = getUIVersion() === 'classic'
    if (isNowClassic !== IS_CLASSIC_UI && !versionNavigationPending) {
      window.location.reload()
    }
  }, [currentUser])

  // Another tab of the same browser shares the session cookie, so a view-as
  // start/exit elsewhere flips this tab's identity on the next poll. Reload so
  // the banner, analytics and caches match. The baseline is the value at first
  // user load; a short sessionStorage window guards against reload loops.
  const viewAsBaseline = useRef<boolean | null>(null)
  useEffect(() => {
    if (!selfQuery.isSuccess || !currentUser) return
    const active = !!currentUser.viewAs?.active
    if (viewAsBaseline.current === null) {
      viewAsBaseline.current = active
      return
    }
    if (viewAsBaseline.current === active || versionNavigationPending) return
    try {
      const last = Number(sessionStorage.getItem(VIEW_AS_RELOAD_KEY))
      if (Date.now() - last < 10_000) return
      sessionStorage.setItem(VIEW_AS_RELOAD_KEY, String(Date.now()))
    } catch {
      // storage unavailable: reload anyway
    }
    window.location.reload()
  }, [selfQuery.isSuccess, currentUser])

  // --- Derived state ---
  const permissions = currentUser?.permissions ?? null

  const { hasGlobalViewPerm, hasGlobalEditPerm, hasGlobalAdminPerm } =
    useMemo(() => {
      const globals = permissions?.global ?? []
      return {
        hasGlobalViewPerm: globals.some(
          (g) =>
            g.role === GlobalRoleType.GLOBAL_VIEW ||
            g.role === GlobalRoleType.GLOBAL_EDIT ||
            g.role === GlobalRoleType.GLOBAL_ADMIN,
        ),
        hasGlobalEditPerm: globals.some(
          (g) =>
            g.role === GlobalRoleType.GLOBAL_EDIT ||
            g.role === GlobalRoleType.GLOBAL_ADMIN,
        ),
        hasGlobalAdminPerm: globals.some(
          (g) => g.role === GlobalRoleType.GLOBAL_ADMIN,
        ),
      }
    }, [permissions])

  const global = useMemo<GlobalPerms>(() => {
    const globals = permissions?.global ?? []
    return {
      view: hasGlobalViewPerm,
      edit: hasGlobalEditPerm,
      admin: hasGlobalAdminPerm,
      cableAdmin: globals.some(
        (g) =>
          g.role === GlobalRoleType.CABLE_ADMIN ||
          g.role === GlobalRoleType.GLOBAL_ADMIN,
      ),
      loggerAdmin: globals.some(
        (g) =>
          g.role === GlobalRoleType.LOGGER_ADMIN ||
          g.role === GlobalRoleType.GLOBAL_ADMIN,
      ),
    }
  }, [permissions, hasGlobalViewPerm, hasGlobalEditPerm, hasGlobalAdminPerm])

  const routing = useMemo(() => {
    if (hasGlobalViewPerm) {
      return {
        home: '/search',
        canViewNavNode: { '/cables': true, '/drafts': true },
      }
    }

    if (IS_CLASSIC_UI) {
      // Classic mode: restore original routing from base repo
      if (permissions?.organizations) {
        const filteredPerms = permissions.organizations.filter(
          (p) => p.role !== null,
        )
        if (filteredPerms.length > 1)
          return { home: '/orgs', canViewNavNode: { '/cables': true } }
        if (filteredPerms.length === 1)
          return {
            home: `/orgs/${filteredPerms[0].id}`,
            canViewNavNode: { '/cables': true },
          }
      }
      if (permissions?.projects) {
        const filteredPerms = permissions.projects.filter(
          (p) => p.role !== null,
        )
        if (filteredPerms.length > 1)
          return { home: '/projects', canViewNavNode: { '/cables': false } }
        if (filteredPerms.length === 1)
          return {
            home: `/projects/${filteredPerms[0].id}`,
            canViewNavNode: { '/cables': false, '/drafts': false },
          }
      }
      return { home: '/', canViewNavNode: {} as Obj }
    }

    if (permissions?.organizations) {
      const filteredPerms = permissions.organizations.filter(
        (p) => p.role !== null,
      )
      if (filteredPerms.length > 1) {
        return { home: '/dashboard', canViewNavNode: { '/cables': true } }
      } else if (filteredPerms.length === 1) {
        return {
          home: '/dashboard',
          canViewNavNode: { '/cables': true },
        }
      }
    }

    if (permissions?.projects) {
      const filteredPerms = permissions.projects.filter((p) => p.role !== null)
      if (filteredPerms.length > 1) {
        return { home: '/dashboard', canViewNavNode: { '/cables': false } }
      } else if (filteredPerms.length === 1) {
        return {
          home: '/dashboard',
          canViewNavNode: { '/cables': false, '/drafts': false },
        }
      }
    }

    return { home: '/dashboard', canViewNavNode: {} as Obj }
  }, [permissions, hasGlobalViewPerm])

  // --- Mutations ---
  const patchUserMutation = useMutation({
    mutationFn: (changes: Operation[]) => {
      if (changes.length === 0) return Promise.resolve()
      return userApi.patch((currentUser?.id as UserId) ?? 'me', changes)
    },
  })

  const loginMutation = useMutation({
    mutationFn: userApi.login,
    onSuccess: (res) => {
      const user = res?.user as UserWithPermissions
      queryClient.setQueryData(queryKeys.self.all(), { user })

      try {
        Sentry.setContext('user', {
          id: user?.id,
          email: user?.email,
        })
        setUser({ email: user?.email })
      } catch {
        // Sentry may be blocked by adblocker
      }
    },
  })

  // --- Actions ---
  const hasPermission = (
    action: keyof typeof perms,
    context: HasPermissionContext,
  ): boolean => {
    if (currentUser) return rootHasPermission(currentUser, action, context)
    return false
  }

  const login = async (
    username: string,
    password: string,
    rememberMe = false,
  ) => loginMutation.mutateAsync({ username, password, rememberMe })

  const logout = async () =>
    userApi.logout().then(() => {
      // The IRC socket is a module singleton: drop the previous user's
      // authenticated connection (#4676).
      resetSocket()
      resetCookies()
      // Don't leak this user's UI version to the login screen (shared
      // machines) -- matches switchToUser's localStorage handling.
      clearUIVersion()
      try {
        getCurrentScope().setUser(null)
      } catch {
        // Sentry may be blocked by adblocker
      }
      // Notify the self query observer before clearing so currentUser derives
      // to null immediately -- queryClient.clear() alone doesn't trigger a
      // re-render for active observers (TanStack/query#7761).
      queryClient.setQueryData(queryKeys.self.all(), null)
      queryClient.clear()
    })

  const switchToUser = async (username: string) => {
    const res = await userApi.switchToUser(username)

    if (res?.error) {
      throw res.error
    }

    resetCookies()
    try {
      getCurrentScope().setUser(null)
    } catch {
      // Sentry may be blocked by adblocker
    }
    // Notify the self query observer before clearing so derived state updates
    queryClient.setQueryData(queryKeys.self.all(), null)
    queryClient.clear()

    const user = res?.user
    queryClient.setQueryData(queryKeys.self.all(), { user })

    // Sync UIVersion localStorage from the new user's modernUi preference.
    // Clears the previous user's stored choice so the switched-to user gets
    // their own setting (or the app default, classic, if they haven't set one).
    const newModernUi = resolveModernUiPref(user?.prefs)
    if (newModernUi === true) {
      setUIVersion('modern')
    } else if (newModernUi === false) {
      setUIVersion('classic')
    } else {
      clearUIVersion()
    }

    try {
      setUser({ email: user?.email?.[0] ?? user?.username })
    } catch {
      // Sentry may be blocked by adblocker
    }

    if ((getUIVersion() === 'classic') !== IS_CLASSIC_UI) {
      // The target user's UI version differs from the loaded one: navigate
      // immediately and suppress the sync effect's reload, which would
      // otherwise fire first and strand the switched user on the previous
      // user's (possibly permission-restricted) route.
      versionNavigationPending = true
      window.location.href = '/'
    } else {
      setTimeout(() => {
        window.location.href = '/'
      }, 500)
    }
    return user
  }

  // View-as start/exit replace the server session (the API regenerates it), so
  // the SPA drops all cached data and does a full navigation to '/'.
  const leaveForHome = () => {
    queryClient.setQueryData(queryKeys.self.all(), null)
    queryClient.clear()
    versionNavigationPending = true
    window.location.href = '/'
  }

  const toastError = (title: string, err: unknown) =>
    toaster.create({
      type: 'error',
      title,
      description: err instanceof Error ? err.message : String(err),
    })

  const startViewAs = async (email: string) => {
    try {
      await userApi.startViewAs(email)
    } catch (err) {
      toastError('Could not start view-as', err)
      return
    }
    leaveForHome()
  }

  // apiUtils rejects on any non-2xx. 400 (not in a view-as session) and 401
  // (revoked; the guard destroyed the session) mean the session is over, so
  // leave either way. Anything else (503, network) keeps the session: stay.
  const exitViewAs = async () => {
    try {
      await userApi.endViewAs()
    } catch (err) {
      const status =
        err && typeof err === 'object' && 'httpStatus' in err
          ? err.httpStatus
          : undefined
      if (status !== 400 && status !== 401) {
        toastError('Could not exit view-as', err)
        return
      }
    }
    leaveForHome()
  }

  return {
    authLoaded,
    currentUser,
    permissions,
    global,
    login,
    logout,
    patchUserMutation,
    routing,
    switchToUser,
    startViewAs,
    exitViewAs,
    hasPermission,
    hasGlobalViewPerm,
    hasGlobalEditPerm,
    hasGlobalAdminPerm,
  }
}

// -- Deprecated compat re-exports (from useUserContextCompat.tsx) ------

export type { IUserContext, Prefs } from './useUserContextCompat'
export { useUserContext } from './useUserContextCompat'

/** @deprecated Use authInitialState instead */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- deprecated compat: prefs field removed; use defaultPrefs from useUserPrefsContext
export const initialState = authInitialState as any

/** @deprecated Use ProvideAuthContext instead */
export const ProvideUserContext = ProvideAuthContext

// -- Static data exports -----------------------------------------------

export const temperatureOptions = [
  { name: 'C', value: 'C' },
  { name: 'F', value: 'F' },
]

export const timezoneOptions = timezones.map((tz) => ({
  value: tz.tzCode,
  label: tz.name,
}))

export const depthOptions = [
  { name: 'in', value: 'in' },
  { name: 'cm', value: 'cm' },
  { name: 'ft', value: 'ft' },
  { name: 'm', value: 'm' },
]

export const coordinateOptions = [
  { name: 'Decimal Degrees', value: 'DD', desc: '(61.1499, -149.8783)' },
  { name: 'DMS', value: 'DMS', desc: '(61° 8\' 59" N, 149° 52\' 41" W)' },
]
